// The names a chart puts on the water (R548).
//
// Card 19 names the ridges and the trenches, card 27 names a bank of deposit
// fields, and card 35 names the regions on its ribbons. All four pools ride
// the compile-time lane from `data/identity/names.toml`, and all four are
// drawn the same way: the biggest feature takes the first name, and no name
// is drawn twice while the pool lasts.
//
// **THE DRAW MUST BE DETERMINISTIC.** Two commanders looking at the same
// world must read the same name for the same ridge, or the names are worse
// than no names at all. `mulberry32(seed)` from `world.ts` is the whole
// source of chance here, and the seed is the world's own id. Nothing reads
// `Math.random`, and nothing depends on the order a caller collected its
// features in: the list is sorted by size and then by id before the first
// name is taken.

import { mulberry32 } from '../world';
import { toRoman } from './roman';

/** What the assignment needs of a feature. `id` must be unique in the list. */
export interface NamedFeature {
  id: number;
  size: number;
  /** `'ridge'`, `'trench'` or `'bank'`. Any other kind takes no name. */
  kind: string;
}

/** The four pools, in the order `data/identity/names.toml` states them. */
export interface NamePools {
  ridge: readonly string[];
  trench: readonly string[];
  bank: readonly string[];
  region: readonly string[];
}

/**
 * A name a Clan War champion gave a trench (R1251, numbers.md §14.2). The
 * server says which body by its lowest cell; the caller turns that into a
 * feature id before it asks for the names.
 */
export interface TrenchGrant {
  name: string;
  /** The Clan War number. */
  war: number;
  /** The winning clan's name. */
  clan: string;
}

/** What `assignFeatureNames` hands the chart. */
export interface FeatureNames {
  /** The name of a feature by its id, or undefined when it took none. */
  nameOf(id: number): string | undefined;
  /** The grant of a named trench by its feature id, or undefined. */
  grantOf(id: number): TrenchGrant | undefined;
  /** The region names, in draw order. Card 35 takes the first six. */
  regions: string[];
  /** The name of the k-th bank, counting from 0. */
  banks(k: number): string;
}

/**
 * A copy of `pool`, shuffled by `rand`. Fisher-Yates, walked downward, so
 * the draw order is a function of the seed and the pool alone.
 */
function shuffled(pool: readonly string[], rand: () => number): string[] {
  const out = pool.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const swap = out[i];
    out[i] = out[j];
    out[j] = swap;
  }
  return out;
}

/**
 * The k-th name of a draw order, with the pool wrapped when it runs dry.
 *
 * A world holds more ridges than the pool holds names. The second pass round
 * a pool adds a Roman numeral — "Vale Ridge II" — because that is what the
 * historical reading writes and because it keeps every name unique.
 */
function nameAt(order: readonly string[], k: number): string {
  if (order.length === 0) return '';
  const base = order[k % order.length];
  const round = Math.floor(k / order.length);
  return round === 0 ? base : `${base} ${toRoman(round + 1)}`;
}

/**
 * Where the stream of each pool starts in the world's `mulberry32` sequence
 * (R1440).
 *
 * Before R1440 the four pools shared ONE stream in this order, so a pool's
 * shuffle started after every draw of the pools before it, and an edit of
 * the ridge pool renamed every trench. The offsets are those starts at the
 * pool lengths of that day (20 ridge, 20 trench and 18 bank names), frozen,
 * so no world lost a name when the streams split. They must never follow
 * the pool lengths: an edit of one pool then moves the names of that pool
 * alone. `feature-names.test.ts` pins the draw of worlds 1 and 2.
 */
const STREAM_START = { ridge: 0, trench: 19, bank: 38, region: 55 } as const;

/** The world's stream, advanced to `start`. */
function streamAt(seed: number, start: number): () => number {
  const rand = mulberry32(seed);
  for (let i = 0; i < start; i++) rand();
  return rand;
}

/**
 * The draw order of each pool for one world: a function of the seed and of
 * that pool alone.
 */
export function drawOrders(pools: NamePools, seed: number): Record<keyof NamePools, string[]> {
  return {
    ridge: shuffled(pools.ridge, streamAt(seed, STREAM_START.ridge)),
    trench: shuffled(pools.trench, streamAt(seed, STREAM_START.trench)),
    bank: shuffled(pools.bank, streamAt(seed, STREAM_START.bank)),
    region: shuffled(pools.region, streamAt(seed, STREAM_START.region)),
  };
}

/**
 * Draws a name for every feature, and holds the region and bank orders.
 *
 * Features are named biggest first, and ties are settled by the id, so the
 * order never depends on how the caller collected them. Each kind draws from
 * its own pool without replacement; `banks(k)` continues the bank pool after
 * whatever the features took from it, so one pool has one owner and no name
 * is used twice.
 *
 * A trench a grant names takes the granted name and draws nothing (R1251).
 * The random draw skips every pool name a grant already uses, so no map
 * shows one name twice. With no grant the draw is exactly the draw it was.
 */
export function assignFeatureNames(
  features: readonly NamedFeature[], pools: NamePools, seed: number,
  grants: ReadonlyMap<number, TrenchGrant> = new Map(),
): FeatureNames {
  const order: Record<string, string[]> = drawOrders(pools, seed);

  const sorted = features.slice().sort((a, b) => (b.size - a.size) || (a.id - b.id));
  const taken: Record<string, number> = { ridge: 0, trench: 0, bank: 0 };
  const names = new Map<number, string>();
  const granted = new Map<number, TrenchGrant>();
  const used = new Set<string>();
  for (const f of features) {
    const g = f.kind === 'trench' ? grants.get(f.id) : undefined;
    if (g && g.name !== '') { granted.set(f.id, g); used.add(g.name); }
  }
  for (const f of sorted) {
    const pool = order[f.kind];
    if (!pool || pool.length === 0 || !(f.kind in taken)) continue;
    if (names.has(f.id)) continue; // a duplicate id keeps the first name
    const g = granted.get(f.id);
    if (g) { names.set(f.id, g.name); continue; }
    let name = nameAt(pool, taken[f.kind]++);
    // A pool that holds only granted names would loop, so the skip is
    // bounded by one pass round the pool.
    for (let i = 0; used.has(name) && i < pool.length; i++) name = nameAt(pool, taken[f.kind]++);
    names.set(f.id, name);
  }

  const bankBase = taken.bank;
  return {
    nameOf: (id: number) => names.get(id),
    grantOf: (id: number) => granted.get(id),
    regions: order.region.slice(),
    banks: (k: number) => (k < 0 ? '' : nameAt(order.bank, bankBase + k)),
  };
}

/**
 * The grants of the named trenches by feature id (R1251).
 *
 * The server names a trench body by its lowest cell index, and a feature
 * lists its cells in ascending order, so the body is the trench whose
 * first cell is that index. A grant whose cell starts no trench of this
 * mirror names nothing.
 */
export function trenchGrants(
  trenches: readonly { id: number; cells: readonly number[] }[],
  named: readonly { cell: number; name: string; war: number; clan: string }[],
): Map<number, TrenchGrant> {
  const out = new Map<number, TrenchGrant>();
  if (named.length === 0) return out;
  const byCell = new Map<number, number>();
  for (const t of trenches) if (t.cells.length > 0) byCell.set(t.cells[0], t.id);
  for (const n of named) {
    const id = byCell.get(n.cell);
    if (id !== undefined && !out.has(id)) out.set(id, { name: n.name, war: n.war, clan: n.clan });
  }
  return out;
}
